import {addDays, formatISO, startOfDay, subDays} from 'date-fns';
import {
  assertEvent,
  assign,
  fromPromise,
  raise,
  setup,
  type SnapshotFrom,
} from 'xstate';

import {
  createEvent,
  getAvailability,
  getAvailabilityRange,
  noAvailability,
  updateEvent,
} from '../../services/schedularService';

import type {
  IBookingMachineContext,
  IBookingMachineEvents,
  IBookingMachineInput,
  IBookingMachineOutput,
} from './bookingMachine.types';
import {canNavigateNextWeek} from '../../utils/booking';
import type {IAdditionalQuestionAnswer} from '../../types/dto/IAdditionalQuestionAnswer';
import {TZDate} from '@date-fns/tz';
import type {AxiosError} from 'axios';
import {captureSentryMessage} from 'src/utils/sentry';

export const bookingMachine = setup({
  types: {
    context: {} as IBookingMachineContext,
    events: {} as IBookingMachineEvents,
    input: {} as IBookingMachineInput,
    output: {} as IBookingMachineOutput,
  },
  actors: {
    getAvailabilityRange: fromPromise(
      async ({
        input,
      }: {
        input: {templateId: string; eventId?: string; sessionId?: string};
      }) =>
        getAvailabilityRange(input.templateId, {
          event_id: input.eventId,
          session_id: input.sessionId,
        }),
    ),
    getAvailability: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          eventId?: string;
          sourceId?: string;
          startDate: string;
          endDate: string;
        };
      }) =>
        getAvailability(input.templateId, {
          viewer_timezone: input.timezone,
          event_id: input.eventId,
          source_id: input.sourceId,
          start_dt: input.startDate,
          end_dt: input.endDate,
        }),
    ),
    updateEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          date: string;
          startTime: string;
          timezone: string;
          attendee: string;
          firstAvailableDatetime: string;
          reason: string;
          aid: string;
        };
      }) =>
        updateEvent(input.eventId, {
          date: input.date,
          start_time: input.startTime,
          viewer_timezone: input.timezone,
          attendee: input.attendee,
          first_available_datetime: input.firstAvailableDatetime,
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    sendNotification: fromPromise(
      ({
        input,
      }: {
        input: {
          templateId: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          sessionId?: string;
          guestTimezone: string;
        };
      }) =>
        noAvailability(input.templateId, {
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          session_id: input.sessionId,
          guest_timezone: input.guestTimezone,
        }),
    ),
    createEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          date: string;
          startTime: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          firstAvailableDatetime: string;
          sessionId?: string;
          language: string;
          additionalQuestionAnswers: IAdditionalQuestionAnswer[];
        };
      }) => {
        return createEvent({
          template_id: input.templateId,
          viewer_timezone: input.timezone,
          date: input.date,
          start_time: input.startTime,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          first_available_datetime: input.firstAvailableDatetime,
          additional_question_answers: input.additionalQuestionAnswers,
          session_id: input.sessionId,
          guest_language: input.language,
        });
      },
    ),
  },
  guards: {
    isTimeSlotUnavailable: ({context}) => {
      const error = context.error as AxiosError<{errors: string[]}>;
      const errorKey = error?.response?.data?.errors?.[0];
      const unavailableTimeSlotErrorKeys = [
        'UnavailableTimeSlot',
        'TimeSlotNotAvailable',
      ] as const;
      return unavailableTimeSlotErrorKeys.includes(
        errorKey as typeof unavailableTimeSlotErrorKeys[number],
      );
    },
    isSlotsAvailableForCurrentWeek: ({context}) => {
      const {timeSlots, selectedStartDateOfWeek, availabilityRange} = context;

      if (!timeSlots || !selectedStartDateOfWeek || !availabilityRange) {
        throw new Error(
          'Time slots or selected start date of week or availability range is not found',
        );
      }

      const weekStart = formatISO(selectedStartDateOfWeek, {
        representation: 'date',
      });
      const weekEnd = formatISO(addDays(selectedStartDateOfWeek, 6), {
        representation: 'date',
      });

      const hasAvailableSlotsInCurrentWeek = Object.keys(
        timeSlots.free_time_slots || {},
      ).some((dateKey) => {
        return dateKey >= weekStart && dateKey <= weekEnd;
      });

      return hasAvailableSlotsInCurrentWeek;
    },
    isNewBooking: ({context}) => !context.eventDetails,
    isNoSlotsAvailableTillLastWeek: ({context}) => {
      const {timeSlots, selectedStartDateOfWeek, availabilityRange, timezone} =
        context;

      if (!timeSlots || !selectedStartDateOfWeek || !availabilityRange) {
        throw new Error(
          'Time slots or selected start date of week or availability range is not found',
        );
      }
      const noSlotsAvailable =
        Object.keys(timeSlots.free_time_slots || {}).length === 0;

      const canNavigateToNextWeek = canNavigateNextWeek(
        timezone,
        selectedStartDateOfWeek,
        availabilityRange,
        noSlotsAvailable,
      );

      return noSlotsAvailable && !canNavigateToNextWeek;
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCMD2qDWBLAdlAxADYCGeArsTAHRkAOExALmANoAMAuoqLarFoyyoc3EAA9EbADQgAnpIC+CmWky4oVYgDdiWEsj0DZAJVIwAMqmIR1+CMLBVcWzI9XY8mnXuIHCR0zwwS2t1BGdUAGMmIRx2DnjRXn5BYVEJBABaACYARgAOKgB2XNK2IrZ87IA2AGYigBZ8mXkEXMqATipaqo6AVg78tj7a2obqpRV0Dw1tXX1DRhMzYKsbPHwwACct1C2qWhJGADM9gFsqd3Uved9F5aCQ9ahwnBdo1LjORKQQZIFYulEH0+kUqO02D1yj0GkVsi1ELlsmw2OCmo18n18g16mNJiArp4GMwACpYM5gAAKWEiGG2+FgjGIW0YABEmGAAPLHADqYDAGCokQAFisfjw+AC0r8MjlxlRsjiGrkxn0Gh1RnCEW0GrqFY1IdVQdV1bVsvjCRpiWAyRTqbT6dahaKguK-pLPkCsmbagrqmwOtUSrUAyUQdrch1sr62Ebair+kUOijahbptdrbaqTS6Vt8AB3fmCnBgMSMN3-T0yxCZEYNYodRvJ7FFI1seFyRFVapUANI7EdCpFWpqtNqIkcrP23MFosHLZgLQVj2A6tZNW5KjVZH9EMdFX1DutPIoqiN-1wqOt6r5MczKiZ8nZh37OY+PxGJ62ewlpxvVyXOmE6kk+07bDc773F+eCvO8MTCPEy4pKuoAZPkvpYrk1RxiqRT5EMzSdm0N6+iGuRwrqeH5B0d4ZpOoE5uBb4LP4SzQQQ2y7PshxMKcWwXJaD70XajGvt4LGfmsYQRB8sSIZwSQrtKqGIK22TdNkTa4dhuSwhGDSVL22RDJU274cqfS0cBNoMS+EESWxayQPgSFSiIa4jme7Yonh7ToViRQRh0TS9sq-SxmwTR9NkRRWVawnPrm9l3KxshPM5LC5FwvyVih4jAlUZ5VLGKo1OhRSBURkYhZFBR9HG0adHFQkgSJdnMSlknWBl2TZRKyHKflCDYaifa6rUgbDNkNTasipRUCCRrYu0mnUbFygEkB8WtYlTHiZ1SwAKI7Hs+ALowWyyK5VYqQgtTVF0Joohqw5NMZ2L6ZpCpqgMumXvd5obYJj5tUlHUfksADKhCoIwsD4IIFKwDDjBULAZDIGcAjXXlGSeVhjT1cF7YPUis2Rj2G4Rb5D0as1IO7WJtwQ7I0Ow-DiNwCjaMY1j5ZZYpA3ubdTRdPUaoVPuaoNNN5PUeC0UmrTI65JZQNbS1Nmg+BnPI7DADCwjHFg-HwTgCNPnrqPo5j2MKTlSnC0N+QFFQ2J9Lpu5TQ9s3xmCRrIsMQaE7e6vjttWuM1Quso4bODG6bnxnXAIqQGQhBgMYYDELAwgAGLnFQUCoDjg0ZH0wwKrVRojMZypBrNFe+iaHvZPVJRTfTCVgUzkGpQAcrDWDHLI+A4EPI8ABJ8Kjxel07GQqg0fRngFr34a2lWtBTqLt9Girti7unNeD9yBDAx1ccnF1Xfb-VuV6APFMZYbJm30UxRG5T1oMkLTbqP1prNXHoIEeVA+JnDHhPWQPNbbljvu6IWXpyIhWVE0cY9UTTi3JjiN2SZFqYjyMOYB0DYF80EBsH8jgIh0kAuHKgIDh4wJtuQ6S-5ZIIW+Ag3KZdEBENGkGTSg4Rz5Dwg0X28plSlAsgZF2g4SGgOYbzAQFCOInW4kcCBdD7yMLASwlRbC4KfHkn1RBD81yRkbGeIoapMTDAGEePh0YexJkhFiXU3kJhhx0aQzip1dFKLgfPL0c0wQjnbFCKoaofZETyLg0RwUPYy3yHULxUx6GWgLvxfAyBiC0mCWuTI8ZSJtkGHhMYlFG5YQVGMM0VQQwV1yM1TJhcIH4C0fo+BpieELxrNhdSSsUnjEHFGHEs1qI9k0iUVWMsVaxmaVtLJFxOmqLsA4P8LhaGCRafxMhBiYIyTNiYwW5jbqZCDF0GoMsqaRmMrkbUox2gLVpkabCxoGgLPDksvZjBVl+I0bxQu2zFmFxWYYqIRyuHdMdl6IpyItw9HaB7ciyJqgPPaD2bcsYPZ2MbJiT5Mxvn-JctwmFhS8hUFhK2AyOIPZ0q3nwwMm5-TYWqO0Cq90mneOuDsi4xLMrQqQeS082JRb1IqnUGasSmVbljDpcow42UEvUN886l1IbKMYO00FmqClnLVL6GKSImxwkiuqWa-RCgXLeaUEcN5lV4G+WQHA4MM5ZjZlqhcxwwCMBFAAQX2izIuJdSVCrOf0BUmJqLRU5VURoVTJm1OMo1RpzUFywFThAdOYAlnJwzcKNOGcs453zkCvJGA9VDWjGCNUfYSjBXwWi2J6oujeWiS2yEFU00pwLVmjOqqe2FsztnXOOBc3pszdm4to6aD0A5JWtCN5igJPqhE6KfRxkUrruy0Rrdu35qHQOg9fbh0lrHecC2SNuaRCNibM4C7gQyy3PXQYdqJUMoQJpeq3RWyYh6PUSEod0n3gnb27NR7J39vOBquBqyqEbIAoJUDh7C7IZPUsmDrCDnsMhQkUNpyhojEmeqSMWIRjoQKA896Z5BGRRNK2Ho+7IM5tQ4O9D0HNV-PUQcTRQKNZofA6x49gn+KYf2S8Q5xioUnJus7dsvYKrRnVHheqE0HklSKoMIYVyUkqiY2BqDuz+X4dk4vWZxQpES33EORxn7LVbhqA9GKUZMGA2A9cAThm+XqJcgLB2Yahr9k3HCFJQxPZ1DNBa+6CoDLtgKJLAMat3OeE8yx3ZzrXWRw9VkrOwmM7J29b64UAbmb3By2x7NwaH1tHqD2KNAN3rjV9qeHS9QZbjADPplDuy1UmAq15sTnqfWXVy8xjDnH1DVdNRZ5TGppGLQtSUXswxxqtgJiaJQG1x4QDgKIS0MncY1gqoUAh-pIrBWmh0bUZStygg69uZeMyT6BrPisdiB3eEIArluLT1Fd3v0jF-UR4JEsTQMv0CuSXNr0IZj3D7vTvRLZqAGIMKpQyqw3VVM0qIKhJJvOUZM4wu47R7slFm73-MEbQqiEZGpkwcsDHhCMmCzzJh6FirCgYgPQ-vLD0SZOoJOQgPDr0mI3YglKPR47f2IxJkKOMf0Ms4TUuMsTyOpPT6pUvnsEXa5or1kPAMKM0YPYjk+pcn6+5YSaQBmrqc-PNdGA9fASnpnERPJcxvaM7PyqzQDIUSWJ4YoBmCh87l1l7ftRe1rs4tAli69uk9YoIJhGDhltuP3gxigBiD5LUPdvbJJRjgbW9ic8o9K9NFMEgZRkvTCtRCRqJ6iENKK3D2Bfta9wcrIQeiiE+BfO5S03znFQB0Itvfo9ZhghZGJFM0BRnuldSufMA2utj9-LiabokZVaB1N5FL+jQip-0VMvZeQDw8aEtBqyIkQ4Au-vm7toYvMRnYqsvRnekiKjHkxqbFlQkRpEw9ksNAAkb879YAH8zEn88gs8TcAxTJl5twm1jxxhfRmwzI7kzRowFEmEN9EQdIFMEkqV8J8Igpv0HpCE8JkxsIcRcCwEIF8C2hfJKUagbEkQ8hMJMdjwzUjJd88g0cP96DAksMoAmDyIAwiCkwSDSDZpFRNx9wlo6hyhqJhCqB-kmDpp4xugK5iYIkdwP04lfRiCkljJUkHUoAlkmDMhhxd56oUkUR1QZZNJZpLwFpMRYQrcuUQDtEVVWlzhrCIsFo244R2gmVRhbM7kexmxYRRgIsXZ1ofDeUflVFAjCpTtHCLsXDv9yhNwqVKCJpA5RELCiV1FrCsRvp8Jp8CgpZVYUDEB4xyI3YCh0IztcJEieceUQUethtWZNVrDcRvoowRwbd6hqoLUBgdDsIXZkRYsgwSjC4MtA03UnwPVrD5cypz8Kg2xYRuC+EqjKUyIsRqJSg25UxL8qBUsrDXdDsEBYRURrkd8Ul-4AFXDFRQpik41GiockN+s0sLhUtp1S1+ImCPZLkdIJCQwRwsRxl5p7osQgwnMLwut2Mei8t-jBtUibjPscVWCwSChtxT9xEcjJiSgW4DIK4lMUSRNvMuJxDKgwRbldJBEShFRah1N0IFQsIDI2Vpo5FqSvMaAXVlissUZyt0TxCegBk-YqhdRdJoxxlPJVM+Smh8cfj+M-iB0b5RsDMMT+jsSEcVp1I7k5cIjaDx9GVtwipyJqJIo7lAYlAgA */
  context: ({input}) => input,
  id: 'booking',
  initial: 'availabilityRangeLoading',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => {
          return event.language;
        },
      }),
    },
  },
  states: {
    availabilityRangeLoading: {
      invoke: {
        id: 'booking.availabilityRangeLoading:invocation[0]',
        input: ({context: {eventDetails, session, eventTemplate}}) => {
          const templateId =
            eventTemplate?.id || eventDetails?.scheduled_from_template_id;
          if (templateId) {
            return {
              templateId,
              eventId: eventDetails?.id,
              sessionId: session?.id,
            };
          }
          throw new Error('Template id not found');
        },
        onDone: {
          target: 'dateTimePicker',
          actions: assign({
            availabilityRange: ({event}) => event.output,
            selectedStartDateOfWeek: ({event, context}) => {
              const startAt = event.output.start_at;
              if (!startAt || isNaN(startAt)) {
                captureSentryMessage(
                  'Invalid start_at from Availability Range API',
                  {
                    level: 'error',
                    tags: {
                      product: 'scheduler',
                    },
                    extra: {
                      apiParams: {
                        templateId: context.eventTemplate,
                        sessionId: context.session?.id,
                        eventId: context.eventDetails?.id,
                      },
                      timezone: context.timezone,
                      rangeApiResponse: event.output,
                      eventDetails: context.eventDetails,
                      eventTemplate: context.eventTemplate,
                    },
                  },
                );
              }
              return new TZDate(startAt * 1000, context.timezone);
            },
          }),
        },
        onError: {
          target: 'availabilityRangeError',
          actions: assign({
            error: ({event}) => event.error,
          }),
        },
        src: 'getAvailabilityRange',
      },
    },
    dateTimePicker: {
      initial: 'availabilityLoading',
      on: {
        'startDateOfWeek.change': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            selectedStartDateOfWeek: ({event}) => event.date,
            selectedTimeSlot: undefined,
          }),
        },
        'date.change': {
          actions: assign({
            selectedDate: ({event}) => event.date,
            selectedTimeSlot: undefined,
          }),
          target: 'dateTimePicker.availabilitySlots',
        },
        'week.next': {
          actions: raise(({context: {selectedStartDateOfWeek, timezone}}) => {
            if (!selectedStartDateOfWeek) {
              throw new Error('Selected start date of week is not found');
            }
            return {
              type: 'startDateOfWeek.change',
              date: new TZDate(addDays(selectedStartDateOfWeek, 7), timezone),
            };
          }),
        },
        'week.prev': {
          actions: raise(({context: {selectedStartDateOfWeek, timezone}}) => {
            if (!selectedStartDateOfWeek) {
              throw new Error('Selected start date of week is not found');
            }
            return {
              type: 'startDateOfWeek.change',
              date: new TZDate(subDays(selectedStartDateOfWeek, 7), timezone),
            };
          }),
        },
      },
      states: {
        availabilityLoading: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                eventTemplate,
                timezone,
                session,
                availabilityRange,
                selectedStartDateOfWeek,
              },
            }) => {
              const templateId =
                eventTemplate?.id || eventDetails?.scheduled_from_template_id;
              try {
                if (
                  templateId &&
                  availabilityRange &&
                  selectedStartDateOfWeek
                ) {
                  const currentDate = selectedStartDateOfWeek;
                  const addedDate = addDays(currentDate, 7);
                  const startDate = formatISO(startOfDay(currentDate), {
                    representation: 'complete',
                  });
                  const endDate = formatISO(startOfDay(addedDate), {
                    representation: 'complete',
                  });

                  return {
                    templateId,
                    timezone,
                    eventId: eventDetails?.id,
                    sourceId: session?.source_id,
                    startDate,
                    endDate,
                  };
                }
                throw new Error('Template id or availabilityRange not found');
              } catch (error) {
                if (selectedStartDateOfWeek) {
                  captureSentryMessage(
                    'Error setting availability api params',
                    {
                      level: 'error',
                      tags: {product: 'scheduler'},
                      extra: {
                        error,
                        timezone,
                        eventDetails,
                        eventTemplate,
                        session,
                        availabilityRange,
                        selectedStartDateOfWeek,
                        weekStartDate: startOfDay(selectedStartDateOfWeek),
                        weekEndDate: startOfDay(
                          addDays(selectedStartDateOfWeek, 7),
                        ),
                      },
                    },
                  );
                }
                throw error;
              }
            },
            onDone: {
              target: 'availabilityLoaded',
              actions: assign({
                timeSlots: ({context, event}) => ({
                  free_time_slots: {
                    ...context.timeSlots?.free_time_slots,
                    ...event.output.free_time_slots,
                  },
                }),
                selectedDate: ({context, event}) => {
                  if (!context.selectedStartDateOfWeek) {
                    throw new Error('Selected start date of week is not found');
                  }

                  return Object.keys(event.output?.free_time_slots || {})[0];
                },
                selectedTimeSlot: undefined,
              }),
            },
            onError: {
              target: 'availabilityError',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'getAvailability',
          },
        },

        availabilityLoaded: {
          always: [
            {
              target: 'availabilitySlots',
              guard: {
                type: 'isSlotsAvailableForCurrentWeek',
              },
            },
            {
              target: 'availabilityNotify',
              reenter: true,
              guard: 'isNoSlotsAvailableTillLastWeek',
            },
            {
              target: 'availabilityEmpty',
              reenter: true,
            },
          ],
        },

        availabilityError: {
          on: {
            retry: {
              target: 'availabilityLoading',
            },
          },
        },

        availabilitySlots: {
          on: {
            'timeslot.submit': [
              {
                target: '#booking.bookingForm',
                actions: assign({
                  selectedTimeSlot: ({event}) => event.timeSlot,
                }),
                guard: {
                  type: 'isNewBooking',
                },
              },
              {
                target: 'timeslotConfirmation',
                actions: assign({
                  selectedTimeSlot: ({event}) => event.timeSlot,
                }),
              },
            ],
          },
        },

        availabilityEmpty: {},

        timeslotConfirmation: {
          on: {
            'timeslot.submit': {
              target: 'timeslotConfirmation',
              actions: assign({
                selectedTimeSlot: ({event}) => event.timeSlot,
              }),
            },
            'rescheduleReasonForm.go': {
              target: '#booking.rescheduleForm',
              reenter: true,
            },
          },
        },

        availabilityNotify: {
          on: {
            'notifyHost.go': {
              target: '#booking.notify',
              reenter: true,
            },
          },
        },
      },
    },
    availabilityRangeError: {
      on: {
        retry: {
          target: 'availabilityRangeLoading',
        },
      },
    },
    bookingSuccess: {
      type: 'final',
      entry: assign({
        finalState: 'BookingSuccess',
      }),
    },
    notifySuccess: {
      type: 'final',
      entry: assign({
        finalState: 'NotifySuccess',
      }),
    },
    notify: {
      initial: 'form',
      states: {
        form: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },

        submitting: {
          invoke: {
            input: ({
              context: {session, eventTemplate, eventDetails, timezone},
              event,
            }) => {
              assertEvent(event, 'notify.submit');
              const templateId =
                eventTemplate?.id || eventDetails?.scheduled_from_template_id;
              if (!templateId) {
                throw new Error('Template id not found');
              }
              return {
                templateId,
                guestName: event.guestName,
                guestEmail: event.guestEmail,
                guestPhone: event.guestPhone,
                sessionId: session?.id,
                guestTimezone: timezone,
              };
            },
            onDone: {
              target: '#booking.notifySuccess',
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'sendNotification',
          },
        },

        error: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },
      },
    },
    bookingForm: {
      initial: 'form',
      on: {
        back: {
          target: '#booking.dateTimePicker.availabilitySlots',
        },
      },
      states: {
        form: {
          on: {
            'form.submit': {
              target: 'submitting',
            },
          },
        },
        submitting: {
          invoke: {
            input: ({
              context: {
                eventTemplate,
                timezone,
                selectedDate,
                selectedTimeSlot,
                timeSlots,
                session,
                language,
              },
              event,
            }) => {
              assertEvent(event, 'form.submit');
              const firstDate = Object.keys(
                timeSlots?.free_time_slots || {},
              )[0];
              const firstDateTimeList = timeSlots?.free_time_slots[firstDate];
              const startTime = firstDateTimeList?.[0].start_time;
              if (eventTemplate?.id && selectedDate && selectedTimeSlot) {
                return {
                  templateId: eventTemplate?.id,
                  timezone,
                  date: selectedDate,
                  startTime: selectedTimeSlot.start_time,
                  guestName: event.guestName,
                  guestEmail: event.guestEmail,
                  guestPhone: event.guestPhone,
                  firstAvailableDatetime: `${firstDate} ${startTime}`,
                  sessionId: session?.id,
                  language,
                  additionalQuestionAnswers:
                    event.additionalQuestionAnswers || [],
                };
              }
              throw new Error('Template id, date or time not found');
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: assign({
                eventDetails: ({event}) => event.output,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'createEvent',
          },
        },
        error: {
          always: [
            {
              target: 'unavailableTimeSlot',
              guard: 'isTimeSlotUnavailable',
              reenter: true,
            },
            'retrySubmit',
          ],
        },
        retrySubmit: {
          on: {
            'form.submit': {
              target: 'submitting',
              reenter: true,
            },
          },
        },
        unavailableTimeSlot: {
          on: {
            'refetchAvailability.go': {
              target: '#booking.dateTimePicker.availabilityLoading',
              reenter: true,
            },
          },
        },
      },
    },
    rescheduleForm: {
      initial: 'rescheduleReasonForm',
      on: {
        'rescheduleReasonForm.back': {
          target: '#booking.dateTimePicker.timeslotConfirmation',
          reenter: true,
          actions: assign({
            rescheduleReason: undefined,
          }),
        },
      },
      states: {
        rescheduleReasonForm: {
          on: {
            'rescheduleReason.update': {
              actions: assign({
                rescheduleReason: ({event}) => event.reason,
              }),
            },
            'timeslot.confirm': 'rescheduleFormSubmitting',
          },
        },

        rescheduleFormSubmitting: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                timeSlots,
                attendee,
                selectedDate,
                selectedTimeSlot,
                timezone,
                rescheduleReason,
              },
              self,
            }) => {
              const firstDate = Object.keys(
                timeSlots?.free_time_slots || {},
              )[0];
              const firstDateTimeList = timeSlots?.free_time_slots[firstDate];
              const startTime = firstDateTimeList?.[0].start_time;

              const rootMachine = self.system.get('root');
              const rootSnapshot = rootMachine?.getSnapshot();

              // Get values from root context
              const aid = rootSnapshot?.context.aid;

              if (eventDetails?.id && selectedDate) {
                return {
                  eventId: eventDetails?.id,
                  date: selectedDate,
                  startTime: selectedTimeSlot?.start_time || '',
                  timezone: timezone || '',
                  attendee: attendee || '',
                  firstAvailableDatetime: `${firstDate} ${startTime}`,
                  reason: rescheduleReason || '',
                  aid: eventDetails?.candidates?.[0]?.id || aid || '',
                };
              }
              throw new Error('Event id not found');
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: assign({
                eventDetails: ({event}) => event.output,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'updateEvent',
          },
        },

        error: {
          always: [
            {
              target: 'unavailableTimeSlotForReschedule',
              guard: 'isTimeSlotUnavailable',
            },
            {
              target: 'retryRescheduleFormSubmit',

              actions: assign({
                selectedTimeSlot: undefined,
              }),

              reenter: true,
            },
          ],
        },

        unavailableTimeSlotForReschedule: {
          on: {
            'refetchAvailabilityForReschedule.go': {
              target: '#booking.dateTimePicker.availabilityLoading',
              reenter: true,
            },
          },
        },

        retryRescheduleFormSubmit: {
          on: {
            retryRescheduleFormSubmitting: 'rescheduleFormSubmitting',
          },
        },
      },
    },
  },
  output: ({context: {eventDetails, finalState, rescheduleReason}}) => ({
    eventDetails,
    finalState,
    rescheduleReason,
  }),
});

export type IBookingMachineSnapshot = SnapshotFrom<typeof bookingMachine>;
